import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiMinus } = FiIcons;

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Do I need to know coding or be very technical?",
      answer: "No, everything is tool-based, step-by-step, and implementation-focused. Abdul and his team will guide you through every technical aspect. You just need to follow the instructions and take action."
    },
    {
      question: "What if I don't have any product idea right now?",
      answer: "We'll help you pick a niche and offer on Day 1. Abdul has a proven framework to identify profitable niches and create compelling offers that clients will pay for monthly."
    },
    {
      question: "Can I do this part-time along with my job or coaching?",
      answer: "Yes, 2–3 focused hours per day for 7 days is enough to launch. The system is designed for busy professionals who want to build recurring income alongside their existing work."
    },
    {
      question: "Is ₹7,500/month the only running cost?",
      answer: "₹7,500/month covers the core software and tools needed. There might be small additional costs for domains, etc., but one client at ₹5,000/month almost covers all running costs."
    },
    {
      question: "How do the 1-to-1 calls work?",
      answer: "You get unlimited 1-to-1 calls with Abdul within the 1-month mentorship period. These are scheduled via calendar system, but there's no limit on the number of sessions you can book."
    },
    {
      question: "Is this guaranteed income?",
      answer: "Results depend on your effort and implementation. However, the model and math are proven, and Abdul guides you personally. If you follow the system and take action, the income potential is very real."
    },
    {
      question: "What happens after the 7 days and 1 month support?",
      answer: "You'll have a fully functional software business and the skills to scale it. Many students continue growing their revenue independently. Additional support packages are available if needed."
    },
    {
      question: "Why only 5 people per month?",
      answer: "Abdul provides personal 1-to-1 mentorship to ensure quality results. Taking more people would compromise the level of attention each person receives. Quality over quantity is the priority."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="faq-section py-20 px-4 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked <span className="text-blue-600">Questions</span>
          </h2>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="faq-item bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <button
                className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                onClick={() => toggleFAQ(index)}
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <SafeIcon 
                  icon={openIndex === index ? FiMinus : FiPlus} 
                  className={`text-2xl flex-shrink-0 transition-colors duration-200 ${
                    openIndex === index ? 'text-blue-600' : 'text-gray-400'
                  }`}
                />
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-gray-700 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <p className="text-lg text-gray-600 mb-6">
            Still have questions? Get them answered on your application call.
          </p>
          <motion.a
            href="https://abdul.cc/earnwithabdul"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-blue-600 to-blue-800 text-white font-bold text-lg px-10 py-4 rounded-full hover:from-blue-700 hover:to-blue-900 transition-all duration-300 transform hover:scale-105 shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Apply Now & Get Answers
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;